/**
 * @module public/js/utils/emailAttachmentUtils
 * @summary Shared browser helpers for email attachment validation and upload paths.
 */

export const EMAIL_ATTACHMENT_MAX_FILES = 5;
export const EMAIL_ATTACHMENT_MAX_FILE_BYTES = 5 * 1024 * 1024;
export const EMAIL_ATTACHMENT_MAX_TOTAL_BYTES = 10 * 1024 * 1024;
export const EMAIL_TEMP_ATTACHMENT_TTL_MS = 24 * 60 * 60 * 1000;

export const EMAIL_ATTACHMENT_ALLOWED_TYPES = new Set([
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain',
  'text/csv',
  'application/csv',
  'image/png',
  'image/jpeg',
  'image/webp',
]);

const EXTENSION_BY_TYPE = {
  'application/pdf': 'pdf',
  'application/msword': 'doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    'docx',
  'application/vnd.ms-excel': 'xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/vnd.ms-powerpoint': 'ppt',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation':
    'pptx',
  'text/plain': 'txt',
  'text/csv': 'csv',
  'application/csv': 'csv',
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/webp': 'webp',
};

/**
 * Return a human-readable byte count for attachment labels and errors.
 * @param {number} bytes Raw byte count.
 * @returns {string}
 */
export function formatEmailAttachmentSize(bytes) {
  const value = Number(bytes || 0);
  if (!Number.isFinite(value) || value <= 0) return '0 KB';
  if (value < 1024 * 1024) return `${Math.ceil(value / 1024)} KB`;
  const mb = value / 1024 / 1024;
  return `${Number.isInteger(mb) ? mb.toFixed(0) : mb.toFixed(1)} MB`;
}

/**
 * Sanitize a file name for both user display and Cloud Storage object names.
 * @param {string} value Candidate file name.
 * @returns {string}
 */
export function sanitizeEmailAttachmentFileName(value) {
  const raw = String(value || 'attachment').trim() || 'attachment';
  const cleaned = raw
    .replace(/[^\w.\- ]+/g, '-')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 120);
  return cleaned || 'attachment';
}

/**
 * Resolve a stable extension for an attachment file.
 * @param {File | null | undefined} file Candidate file.
 * @returns {string}
 */
export function resolveEmailAttachmentExtension(file) {
  const type = String(file?.type || '').toLowerCase();
  if (EXTENSION_BY_TYPE[type]) return EXTENSION_BY_TYPE[type];

  const name = String(file?.name || '');
  const parts = name.split('.');
  const ext = parts.length > 1 ? parts.pop() : '';
  return (
    String(ext || 'bin')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '') || 'bin'
  );
}

/**
 * Normalize a stored attachment metadata row.
 * @param {unknown} entry Raw metadata row.
 * @returns {{id:string,name:string,storagePath:string,contentType:string,size:number,uploadedByUid:string,uploadedAtISO:string}|null}
 */
export function normalizeEmailAttachmentMeta(entry) {
  const storagePath = String(entry?.storagePath || entry?.path || '').trim();
  const name = sanitizeEmailAttachmentFileName(entry?.name || entry?.fileName);
  if (!storagePath) return null;
  return {
    id: String(entry?.id || '').trim(),
    name,
    storagePath,
    contentType: String(entry?.contentType || '')
      .trim()
      .toLowerCase(),
    size: Number(entry?.size || 0),
    uploadedByUid: String(entry?.uploadedByUid || '').trim(),
    uploadedAtISO: String(entry?.uploadedAtISO || '').trim(),
  };
}

/**
 * Normalize a list of stored attachment metadata rows.
 * @param {unknown[]} attachments Raw metadata rows.
 * @returns {Array<object>}
 */
export function normalizeEmailAttachments(attachments) {
  if (!Array.isArray(attachments)) return [];
  return attachments.map(normalizeEmailAttachmentMeta).filter(Boolean);
}

/**
 * Validate one selected attachment file.
 * @param {File | null | undefined} file Candidate file.
 * @returns {{ok:boolean,reason?:string}}
 */
export function validateEmailAttachmentFile(file) {
  if (typeof File === 'undefined' || !(file instanceof File)) {
    return { ok: false, reason: 'Select a file to attach.' };
  }

  const type = String(file.type || '').toLowerCase();
  if (!EMAIL_ATTACHMENT_ALLOWED_TYPES.has(type)) {
    return {
      ok: false,
      reason:
        'Attach a PDF, Office document, CSV/text file, PNG, JPG, or WEBP image.',
    };
  }

  if (
    typeof file.size === 'number' &&
    file.size > EMAIL_ATTACHMENT_MAX_FILE_BYTES
  ) {
    return {
      ok: false,
      reason: `Each attachment must be ${formatEmailAttachmentSize(
        EMAIL_ATTACHMENT_MAX_FILE_BYTES
      )} or smaller.`,
    };
  }

  return { ok: true };
}

/**
 * Validate a full attachment selection against count and total-byte limits.
 * @param {Array<File|object>} attachments Candidate File objects or metadata rows.
 * @returns {{ok:boolean,reason?:string}}
 */
export function validateEmailAttachmentSelection(attachments = []) {
  const rows = Array.from(attachments || []);
  if (rows.length > EMAIL_ATTACHMENT_MAX_FILES) {
    return {
      ok: false,
      reason: `Attach up to ${EMAIL_ATTACHMENT_MAX_FILES} files per email.`,
    };
  }

  let total = 0;
  for (const row of rows) {
    if (typeof File !== 'undefined' && row instanceof File) {
      const validation = validateEmailAttachmentFile(row);
      if (!validation.ok) return validation;
      total += Number(row.size || 0);
    } else {
      total += Number(row?.size || 0);
    }
  }

  if (total > EMAIL_ATTACHMENT_MAX_TOTAL_BYTES) {
    return {
      ok: false,
      reason: `Attachments must total ${formatEmailAttachmentSize(
        EMAIL_ATTACHMENT_MAX_TOTAL_BYTES
      )} or less.`,
    };
  }

  return { ok: true };
}

/**
 * Build the normalized Storage prefix for durable template attachments.
 * @param {{tenantId:string,templateId:string}} params Template scope inputs.
 * @returns {string} Tenant-scoped attachment prefix ending in a slash.
 */
export function buildEmailTemplateAttachmentPrefix({ tenantId, templateId }) {
  const safeTenantId = preserveIdentityPathSegment(tenantId, 'unknown-tenant');
  const safeTemplateId = sanitizePathSegment(templateId, 'draft');
  return `tenants/${safeTenantId}/email-template-attachments/${safeTemplateId}/`;
}

/**
 * Build a durable template attachment path.
 * @param {{tenantId:string,templateId:string,file:File,idFactory?:()=>string}} params Path inputs.
 * @returns {string}
 */
export function buildEmailTemplateAttachmentPath({
  tenantId,
  templateId,
  file,
  idFactory = () => crypto.randomUUID(),
}) {
  const prefix = buildEmailTemplateAttachmentPrefix({ tenantId, templateId });
  const fileId = sanitizePathSegment(idFactory(), 'attachment');
  const safeName = sanitizeEmailAttachmentFileName(file?.name || 'attachment');
  const extension = resolveEmailAttachmentExtension(file);
  const nameWithExtension = ensureExtension(safeName, extension);
  return `${prefix}${fileId}-${nameWithExtension}`;
}

/**
 * Build a short-lived ad hoc attachment path.
 * @param {{tenantId:string,uid:string,draftId:string,file:File,idFactory?:()=>string}} params Path inputs.
 * @returns {string}
 */
export function buildEmailTempAttachmentPath({
  tenantId,
  uid,
  draftId,
  file,
  idFactory = () => crypto.randomUUID(),
}) {
  const safeTenantId = preserveIdentityPathSegment(tenantId, 'unknown-tenant');
  const safeUid = preserveIdentityPathSegment(uid, 'unknown-user');
  const safeDraftId = sanitizePathSegment(draftId, 'draft');
  const fileId = sanitizePathSegment(idFactory(), 'attachment');
  const safeName = sanitizeEmailAttachmentFileName(file?.name || 'attachment');
  const extension = resolveEmailAttachmentExtension(file);
  const nameWithExtension = ensureExtension(safeName, extension);
  return `email-temp-attachments/${safeTenantId}/${safeUid}/${safeDraftId}/${fileId}-${nameWithExtension}`;
}

/**
 * Upload one durable template attachment.
 * @param {{file:File,tenantId:string,templateId:string,uid:string,app?:object}} params Upload inputs.
 * @returns {Promise<object>} Stored attachment metadata.
 */
export async function uploadTemplateEmailAttachment({
  file,
  tenantId,
  templateId,
  uid,
  app,
}) {
  const validation = validateEmailAttachmentFile(file);
  if (!validation.ok) {
    throw new Error(validation.reason || 'Invalid attachment.');
  }

  const storageLib = await import(
    'https://www.gstatic.com/firebasejs/9.17.2/firebase-storage.js'
  );
  const storage = app ? storageLib.getStorage(app) : storageLib.getStorage();
  const id = crypto.randomUUID();
  const storagePath = buildEmailTemplateAttachmentPath({
    tenantId,
    templateId,
    file,
    idFactory: () => id,
  });
  const fileRef = storageLib.ref(storage, storagePath);
  const uploadedAtISO = new Date().toISOString();
  const uploadResult = await storageLib.uploadBytes(fileRef, file, {
    contentType: file.type || undefined,
    customMetadata: {
      tenantId: String(tenantId || ''),
      templateId: String(templateId || ''),
      uploaderUid: String(uid || ''),
      uploadedAtISO,
      originalName: sanitizeEmailAttachmentFileName(file.name || 'attachment'),
      scope: 'emailTemplateAttachment',
    },
  });
  return {
    id,
    name: sanitizeEmailAttachmentFileName(file.name || 'attachment'),
    storagePath,
    contentType: String(file.type || uploadResult.metadata?.contentType || ''),
    size:
      typeof file.size === 'number'
        ? file.size
        : Number(uploadResult.metadata?.size || 0),
    uploadedByUid: String(uid || ''),
    uploadedAtISO,
  };
}

/**
 * Upload ad hoc attachment files and return short-lived refs for a callable.
 * @param {{files:File[],tenantId:string,uid:string,ttlMs?:number,app?:object}} params Upload inputs.
 * @returns {Promise<Array<object>>}
 */
export async function uploadTempEmailAttachments({
  files = [],
  tenantId,
  uid,
  ttlMs = EMAIL_TEMP_ATTACHMENT_TTL_MS,
  app,
}) {
  const selected = Array.from(files || []);
  const validation = validateEmailAttachmentSelection(selected);
  if (!validation.ok) {
    throw new Error(validation.reason || 'Invalid attachments.');
  }
  if (!selected.length) return [];

  const storageLib = await import(
    'https://www.gstatic.com/firebasejs/9.17.2/firebase-storage.js'
  );
  const storage = app ? storageLib.getStorage(app) : storageLib.getStorage();
  const draftId = crypto.randomUUID();
  const expiresAtISO = new Date(Date.now() + ttlMs).toISOString();
  const refs = [];

  for (const file of selected) {
    const id = crypto.randomUUID();
    const storagePath = buildEmailTempAttachmentPath({
      tenantId,
      uid,
      draftId,
      file,
      idFactory: () => id,
    });
    const fileRef = storageLib.ref(storage, storagePath);
    const uploadResult = await storageLib.uploadBytes(fileRef, file, {
      contentType: file.type || undefined,
      customMetadata: {
        tenantId: String(tenantId || ''),
        uploaderUid: String(uid || ''),
        draftId,
        expiresAtISO,
        originalName: sanitizeEmailAttachmentFileName(
          file.name || 'attachment'
        ),
        scope: 'emailTempAttachment',
      },
    });
    refs.push({
      id,
      name: sanitizeEmailAttachmentFileName(file.name || 'attachment'),
      storagePath,
      contentType: String(
        file.type || uploadResult.metadata?.contentType || ''
      ),
      size:
        typeof file.size === 'number'
          ? file.size
          : Number(uploadResult.metadata?.size || 0),
      uploadedByUid: String(uid || ''),
      expiresAtISO,
    });
  }

  return refs;
}

/**
 * Encode one browser File as callable-safe base64.
 * @param {File} file Attachment file.
 * @returns {Promise<string>} Base64 attachment content.
 */
export async function encodeEmailAttachmentBase64(file) {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  const chunkSize = 0x8000;
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(
      ...bytes.subarray(offset, offset + chunkSize)
    );
  }
  return btoa(binary);
}

/**
 * Upload ad hoc attachments through the authenticated backend callable.
 * @param {{files:File[],tenantId:string,uploadCallable:(payload:object)=>Promise<object>}} params Upload inputs.
 * @returns {Promise<Array<object>>} Server-validated temporary attachment refs.
 */
export async function uploadTempEmailAttachmentsViaCallable({
  files = [],
  tenantId,
  uploadCallable,
}) {
  const selected = Array.from(files || []);
  const validation = validateEmailAttachmentSelection(selected);
  if (!validation.ok) {
    throw new Error(validation.reason || 'Invalid attachments.');
  }
  if (!selected.length) return [];
  if (typeof uploadCallable !== 'function') {
    throw new Error('Attachment upload service is unavailable.');
  }

  const refs = [];
  for (const file of selected) {
    const response = await uploadCallable({
      tenantId: String(tenantId || ''),
      name: sanitizeEmailAttachmentFileName(file.name || 'attachment'),
      contentType: String(file.type || '')
        .trim()
        .toLowerCase(),
      size: Number(file.size || 0),
      contentBase64: await encodeEmailAttachmentBase64(file),
    });
    const ref = response?.data || response;
    if (!ref?.storagePath) {
      throw new Error('Attachment upload returned an invalid reference.');
    }
    refs.push(ref);
  }
  return refs;
}

/**
 * Best-effort delete a list of Storage object paths.
 * @param {string[]} storagePaths Storage paths to delete.
 * @param {{app?:object}} [options] Storage options.
 * @returns {Promise<void>}
 */
export async function deleteEmailAttachmentPaths(
  storagePaths = [],
  { app } = {}
) {
  const paths = Array.from(new Set(storagePaths.filter(Boolean)));
  if (!paths.length) return;
  const storageLib = await import(
    'https://www.gstatic.com/firebasejs/9.17.2/firebase-storage.js'
  );
  const storage = app ? storageLib.getStorage(app) : storageLib.getStorage();
  await Promise.all(
    paths.map((path) =>
      storageLib.deleteObject(storageLib.ref(storage, path)).catch(() => null)
    )
  );
}

/**
 * Ensure a sanitized filename ends with the expected extension.
 * @param {string} fileName Sanitized filename.
 * @param {string} extension Extension without a leading dot.
 * @returns {string}
 */
function ensureExtension(fileName, extension) {
  const ext = String(extension || '').toLowerCase();
  if (!ext) return fileName;
  return fileName.toLowerCase().endsWith(`.${ext}`)
    ? fileName
    : `${fileName}.${ext}`;
}

/**
 * Convert an arbitrary value into a Cloud Storage-safe path segment.
 * @param {string} value Raw segment candidate.
 * @param {string} fallback Fallback when the segment is empty.
 * @returns {string}
 */
function sanitizePathSegment(value, fallback) {
  const safe = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-_]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  return safe || fallback;
}

/**
 * Preserve a case-sensitive tenant or user identity for a Storage path segment.
 * @param {string} value Raw tenant ID or Firebase user ID.
 * @param {string} fallback Fallback when the identity is empty.
 * @returns {string} Trimmed identity with its original case preserved.
 */
function preserveIdentityPathSegment(value, fallback) {
  const exact = String(value || '').trim();
  if (!exact) return fallback;
  const hasControlCharacter = Array.from(exact).some((character) => {
    const characterCode = character.charCodeAt(0);
    return characterCode <= 31 || characterCode === 127;
  });
  if (
    exact === '.' ||
    exact === '..' ||
    exact.includes('\\') ||
    exact.includes('/') ||
    hasControlCharacter
  ) {
    throw new Error('Invalid identity path segment.');
  }
  return exact;
}
